import { cn } from '@/lib/utils';

interface LogoProps {
  /** Tailwind size + text-color classes. Wireframe inherits via currentColor.
   * Examples: "h-8 w-8 text-brand-indigo" (on cream/teal/white surfaces),
   *          "h-8 w-8 text-brand-cream" (on indigo surfaces). */
  className?: string;
  /** When true, render with the bright teal background disc — used in
   * marketing contexts where the logo is the standalone brand mark. When
   * false (default) it's a single-color wireframe driven by currentColor. */
  withBadge?: boolean;
  title?: string;
}

/**
 * AgentCAD logo — icosahedron wireframe, face-on projection.
 *
 * Default rendering is single-color via `currentColor` so each surface
 * picks its own contrast. Pass `withBadge` for the marketing mark which
 * adds a bright teal disc behind the wireframe.
 */
export function Logo({ className, withBadge = false, title }: LogoProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 64 64"
      role="img"
      aria-label={title ?? 'AgentCAD'}
      className={cn(className)}
      fill="none"
    >
      {title ? <title>{title}</title> : null}
      {withBadge && <circle cx="32" cy="32" r="30" fill="#56B6C6" />}

      {/* Icosahedron wireframe — face-on projection */}
      <g
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {/* Outer hexagon silhouette */}
        <polygon
          points="58,32 45,9.5 19,9.5 6,32 19,54.5 45,54.5"
          strokeWidth="2.6"
        />

        {/* Front-facing triangle (closer) */}
        <polygon points="44,25 20,25 32,46" strokeWidth="2.2" />

        {/* Back-facing triangle (farther — softer) */}
        <polygon
          points="32,18 20,39 44,39"
          strokeWidth="1.6"
          strokeOpacity="0.55"
        />

        {/* Hex → front-triangle struts */}
        <line x1="58" y1="32" x2="44" y2="25" strokeWidth="2" />
        <line x1="45" y1="9.5" x2="44" y2="25" strokeWidth="2" />
        <line x1="19" y1="9.5" x2="20" y2="25" strokeWidth="2" />
        <line x1="6" y1="32" x2="20" y2="25" strokeWidth="2" />
        <line x1="19" y1="54.5" x2="32" y2="46" strokeWidth="2" />
        <line x1="45" y1="54.5" x2="32" y2="46" strokeWidth="2" />

        {/* Hex → back-triangle struts (faded) */}
        <line
          x1="58"
          y1="32"
          x2="44"
          y2="39"
          strokeWidth="1.6"
          strokeOpacity="0.55"
        />
        <line
          x1="45"
          y1="9.5"
          x2="32"
          y2="18"
          strokeWidth="1.6"
          strokeOpacity="0.55"
        />
        <line
          x1="19"
          y1="9.5"
          x2="32"
          y2="18"
          strokeWidth="1.6"
          strokeOpacity="0.55"
        />
        <line
          x1="6"
          y1="32"
          x2="20"
          y2="39"
          strokeWidth="1.6"
          strokeOpacity="0.55"
        />
        <line
          x1="19"
          y1="54.5"
          x2="20"
          y2="39"
          strokeWidth="1.6"
          strokeOpacity="0.55"
        />
        <line
          x1="45"
          y1="54.5"
          x2="44"
          y2="39"
          strokeWidth="1.6"
          strokeOpacity="0.55"
        />
      </g>

      {/* Vertex highlights for crispness at small sizes */}
      <g fill="currentColor">
        <circle cx="58" cy="32" r="1.8" />
        <circle cx="45" cy="9.5" r="1.8" />
        <circle cx="19" cy="9.5" r="1.8" />
        <circle cx="6" cy="32" r="1.8" />
        <circle cx="19" cy="54.5" r="1.8" />
        <circle cx="45" cy="54.5" r="1.8" />
      </g>
    </svg>
  );
}
